import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';

const HomePage = lazy(() => import('../pages/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const ServicesPage = lazy(() => import('../pages/services/page'));
const TeamPage = lazy(() => import('../pages/team/page'));
const ScholarshipDetailPage = lazy(() => import('../pages/scholarship-detail/page'));
const NewsDetailPage = lazy(() => import('../pages/news-detail/page'));
const PrivacyPolicyPage = lazy(() => import('../pages/privacy-policy/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const BlogPage = lazy(() => import('../pages/blog/page'));
const BlogDetailPage = lazy(() => import('../pages/blog-detail/page'));

const ContactPage = lazy(() => import('../pages/contact/page'));
const CoursesPage = lazy(() => import('../pages/courses/page'));
const CourseDetailPage = lazy(() => import('../pages/course-detail/page'));
const YCTPage = lazy(() => import('../pages/yct/page'));
const ApplyPage = lazy(() => import('../pages/apply/page'));

// Student Portal Pages
const StudentLoginPage = lazy(() => import('../pages/student/login/page'));
const StudentRegisterPage = lazy(() => import('../pages/student/register/page'));
const StudentDashboardPage = lazy(() => import('../pages/student/dashboard/page'));
const StudentIDCardPage = lazy(() => import('../pages/student/id-card/page'));
const StudentExamPage = lazy(() => import('../pages/student/exam/page'));

// Admin Pages
const AdminLoginPage = lazy(() => import('../pages/admin/login/page'));
const AdminDashboardPage = lazy(() => import('../pages/admin/dashboard/page'));
const AdminStudentsPage = lazy(() => import('../pages/admin/students/page'));
const AdminStudentDetailsPage = lazy(() => import('../pages/admin/students/details/page'));
const AdminProgramsPage = lazy(() => import('../pages/admin/programs/page'));
const AdminFeesPage = lazy(() => import('../pages/admin/fees/page'));
const AdminFeeCollectionPage = lazy(() => import('../pages/admin/fees/collection/page'));
const AdminFeeTypesPage = lazy(() => import('../pages/admin/fees/fee-types/page'));
const AdminIncomePage = lazy(() => import('../pages/admin/income/page'));
const AdminPaymentsPage = lazy(() => import('../pages/admin/payments/page'));
const AdminReceiptsPage = lazy(() => import('../pages/admin/receipts/page'));
const AdminStaffPage = lazy(() => import('../pages/admin/staff/page'));
const AdminReportsPage = lazy(() => import('../pages/admin/reports/page'));
const AdminERPPage = lazy(() => import('../pages/admin/erp/page'));
const AdminAttendanceScanPage = lazy(() => import('../pages/admin/attendance/scan/page'));
const AdminAttendanceRecordsPage = lazy(() => import('../pages/admin/attendance/records/page'));
const AdminSalariesPage = lazy(() => import('../pages/admin/salaries/page'));
const AdminExpensesPage = lazy(() => import('../pages/admin/expenses/page'));
const AdminFinancialReportsPage = lazy(() => import('../pages/admin/financial-reports/page'));
const AdminSettingsPage = lazy(() => import('../pages/admin/settings/page'));
const AdminExamsPage = lazy(() => import('../pages/admin/exams/page'));
const AdminOnlineExamsPage = lazy(() => import('../pages/admin/examinations/online/page'));
const AdminLMSPage = lazy(() => import('../pages/admin/lms/page'));
const AdminHRPage = lazy(() => import('../pages/admin/hr/page'));
const AdminHRDepartmentsPage = lazy(() => import('../pages/admin/hr/departments/page'));
const AdminHREmployeesPage = lazy(() => import('../pages/admin/hr/employees/page'));
const AdminHRLeavePage = lazy(() => import('../pages/admin/hr/leave/page'));
const AdminLibraryPage = lazy(() => import('../pages/admin/library/page'));
const AdminInventoryPage = lazy(() => import('../pages/admin/inventory/page'));
const AdminCommunicatePage = lazy(() => import('../pages/admin/communicate/page'));
const AdminDownloadCenterPage = lazy(() => import('../pages/admin/download-center/page'));
const AdminBackupPage = lazy(() => import('../pages/admin/backup/page'));
const AdminTransportPage = lazy(() => import('../pages/admin/transport/page'));
const AdminHostelPage = lazy(() => import('../pages/admin/hostel/page'));
const AdminCertificatePage = lazy(() => import('../pages/admin/certificate/page'));
const AdminAlumniPage = lazy(() => import('../pages/admin/alumni/page'));
const AdminBehaviourRecordsPage = lazy(() => import('../pages/admin/behaviour-records/page'));
const AdminMultiBranchPage = lazy(() => import('../pages/admin/multi-branch/page'));

// Academic System Pages
const AdminClassesPage = lazy(() => import('../pages/admin/academics/classes/page'));
const AdminSubjectsPage = lazy(() => import('../pages/admin/academics/subjects/page'));
const AdminTimetablePage = lazy(() => import('../pages/admin/academics/timetable/page'));
const AdminLessonPlanPage = lazy(() => import('../pages/admin/academics/lesson-plan/page'));
const AdminCalendarPage = lazy(() => import('../pages/admin/academics/calendar/page'));
const AdminHomeworkPage = lazy(() => import('../pages/admin/academics/homework/page'));

const AdminNotificationsPage = lazy(() => import('../pages/admin/notifications/page'));

// Parent Portal Pages
const ParentLoginPage = lazy(() => import('../pages/parent/login/page'));
const ParentDashboardPage = lazy(() => import('../pages/parent/dashboard/page'));

// Teacher Portal Pages
const TeacherLoginPage = lazy(() => import('../pages/teacher/login/page'));
const TeacherDashboardPage = lazy(() => import('../pages/teacher/dashboard/page'));
const TeacherClassesPage = lazy(() => import('../pages/teacher/classes/page'));
const TeacherAttendancePage = lazy(() => import('../pages/teacher/attendance/page'));
const TeacherGradesPage = lazy(() => import('../pages/teacher/grades/page'));
const TeacherHomeworkPage = lazy(() => import('../pages/teacher/homework/page'));
const TeacherSchedulePage = lazy(() => import('../pages/teacher/schedule/page'));
const TeacherCommunicationPage = lazy(() => import('../pages/teacher/communication/page'));

const NotFoundPage = lazy(() => import('../pages/NotFound'));

const routes: RouteObject[] = [
  { path: '/', element: <HomePage /> },
  { path: '/about', element: <AboutPage /> },
  { path: '/services', element: <ServicesPage /> },
  { path: '/team', element: <TeamPage /> },
  { path: '/scholarship/:id', element: <ScholarshipDetailPage /> },
  { path: '/news/:id', element: <NewsDetailPage /> },
  { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/blog', element: <BlogPage /> },
  { path: '/blog/:slug', element: <BlogDetailPage /> },
  { path: '/contact', element: <ContactPage /> },
  { path: '/courses', element: <CoursesPage /> },
  { path: '/courses/:slug', element: <CourseDetailPage /> },
  { path: '/ar/courses', element: <CoursesPage /> },
  { path: '/ar/courses/:slug', element: <CourseDetailPage /> },
  { path: '/en/courses', element: <CoursesPage /> },
  { path: '/en/courses/:slug', element: <CourseDetailPage /> },
  { path: '/zh/courses', element: <CoursesPage /> },
  { path: '/zh/courses/:slug', element: <CourseDetailPage /> },
  { path: '/yct', element: <YCTPage /> },
  { path: '/ar/yct', element: <YCTPage /> },
  { path: '/en/yct', element: <YCTPage /> },
  { path: '/zh/yct', element: <YCTPage /> },
  { path: '/apply', element: <ApplyPage /> },

  // Student Portal
  { path: '/student/login', element: <StudentLoginPage /> },
  { path: '/student/register', element: <StudentRegisterPage /> },
  { path: '/student/dashboard', element: <StudentDashboardPage /> },
  { path: '/student/id-card', element: <StudentIDCardPage /> },
  { path: '/student/exam/:id', element: <StudentExamPage /> },

  // Admin - Auth
  { path: '/admin/login', element: <AdminLoginPage /> },

  // Admin - Dashboard
  { path: '/admin/dashboard', element: <AdminDashboardPage /> },

  // Admin - Students
  { path: '/admin/students', element: <AdminStudentsPage /> },
  { path: '/admin/students/details', element: <AdminStudentDetailsPage /> },
  { path: '/admin/students/admission', element: <AdminStudentsPage /> },
  { path: '/admin/students/online-admission', element: <AdminStudentsPage /> },
  { path: '/admin/students/disabled', element: <AdminStudentsPage /> },
  { path: '/admin/students/multi-class', element: <AdminStudentsPage /> },
  { path: '/admin/students/bulk-delete', element: <AdminStudentsPage /> },
  { path: '/admin/students/categories', element: <AdminStudentsPage /> },
  { path: '/admin/students/house', element: <AdminStudentsPage /> },
  { path: '/admin/students/disable-reason', element: <AdminStudentsPage /> },
  { path: '/admin/students/cv', element: <AdminStudentsPage /> },

  // Admin - Fees & Finance
  { path: '/admin/fees', element: <AdminFeesPage /> },
  { path: '/admin/fees/collection', element: <AdminFeeCollectionPage /> },
  { path: '/admin/fees/fee-types', element: <AdminFeeTypesPage /> },
  { path: '/admin/income', element: <AdminIncomePage /> },
  { path: '/admin/expenses', element: <AdminExpensesPage /> },
  { path: '/admin/payments', element: <AdminPaymentsPage /> },
  { path: '/admin/receipts', element: <AdminReceiptsPage /> },
  { path: '/admin/salaries', element: <AdminSalariesPage /> },
  { path: '/admin/financial-reports', element: <AdminFinancialReportsPage /> },

  // Admin - Programs & Staff
  { path: '/admin/programs', element: <AdminProgramsPage /> },
  { path: '/admin/staff', element: <AdminStaffPage /> },

  // Admin - Attendance
  { path: '/admin/attendance', element: <AdminAttendanceRecordsPage /> },
  { path: '/admin/attendance/scan', element: <AdminAttendanceScanPage /> },
  { path: '/admin/attendance/records', element: <AdminAttendanceRecordsPage /> },

  // Admin - Academics
  { path: '/admin/academics/classes', element: <AdminClassesPage /> },
  { path: '/admin/academics/subjects', element: <AdminSubjectsPage /> },
  { path: '/admin/academics/timetable', element: <AdminTimetablePage /> },
  { path: '/admin/academics/lesson-plan', element: <AdminLessonPlanPage /> },
  { path: '/admin/academics/calendar', element: <AdminCalendarPage /> },
  { path: '/admin/academics/homework', element: <AdminHomeworkPage /> },

  // Admin - Examinations
  { path: '/admin/examinations/regular', element: <AdminExamsPage /> },
  { path: '/admin/examinations/cbse', element: <AdminExamsPage /> },
  { path: '/admin/examinations/online', element: <AdminOnlineExamsPage /> },

  // Admin - HR
  { path: '/admin/hr', element: <AdminHRPage /> },
  { path: '/admin/hr/employees', element: <AdminHREmployeesPage /> },
  { path: '/admin/hr/departments', element: <AdminHRDepartmentsPage /> },
  { path: '/admin/hr/leave', element: <AdminHRLeavePage /> },
  { path: '/admin/hr/payroll', element: <AdminSalariesPage /> },
  { path: '/admin/hr/attendance', element: <AdminAttendanceRecordsPage /> },

  // Admin - Communicate
  { path: '/admin/communicate', element: <AdminCommunicatePage /> },
  { path: '/admin/communicate/notice', element: <AdminCommunicatePage /> },
  { path: '/admin/communicate/email', element: <AdminCommunicatePage /> },
  { path: '/admin/communicate/sms', element: <AdminCommunicatePage /> },

  // Admin - Resources
  { path: '/admin/resources/download-center', element: <AdminDownloadCenterPage /> },
  { path: '/admin/download-center', element: <AdminDownloadCenterPage /> },
  { path: '/admin/resources/library', element: <AdminLibraryPage /> },
  { path: '/admin/library', element: <AdminLibraryPage /> },
  { path: '/admin/resources/inventory', element: <AdminInventoryPage /> },
  { path: '/admin/inventory', element: <AdminInventoryPage /> },

  // Admin - LMS & ERP
  { path: '/admin/lms', element: <AdminLMSPage /> },
  { path: '/admin/erp', element: <AdminERPPage /> },
  { path: '/admin/online-course', element: <AdminLMSPage /> },

  // Admin - Reports
  { path: '/admin/reports', element: <AdminReportsPage /> },

  // Admin - Settings & System
  { path: '/admin/settings', element: <AdminSettingsPage /> },
  { path: '/admin/backup', element: <AdminBackupPage /> },
  { path: '/admin/system/roles', element: <AdminSettingsPage /> },

  // Admin - Transport & Hostel
  { path: '/admin/transport', element: <AdminTransportPage /> },
  { path: '/admin/hostel', element: <AdminHostelPage /> },

  // Admin - Certificate
  { path: '/admin/certificate', element: <AdminCertificatePage /> },

  // Admin - Alumni
  { path: '/admin/alumni', element: <AdminAlumniPage /> },

  // Admin - Behaviour Records
  { path: '/admin/behaviour-records', element: <AdminBehaviourRecordsPage /> },

  // Admin - Other modules
  { path: '/admin/multi-branch', element: <AdminMultiBranchPage /> },
  { path: '/admin/live-classes/gmeet', element: <AdminLMSPage /> },
  { path: '/admin/live-classes/zoom', element: <AdminLMSPage /> },
  { path: '/admin/front-cms', element: <AdminSettingsPage /> },

  // Admin - Notifications
  { path: '/admin/notifications', element: <AdminNotificationsPage /> },

  // Parent Portal
  { path: '/parent/login', element: <ParentLoginPage /> },
  { path: '/parent/dashboard', element: <ParentDashboardPage /> },

  // Teacher Portal
  { path: '/teacher/login', element: <TeacherLoginPage /> },
  { path: '/teacher/dashboard', element: <TeacherDashboardPage /> },
  { path: '/teacher/classes', element: <TeacherClassesPage /> },
  { path: '/teacher/attendance', element: <TeacherAttendancePage /> },
  { path: '/teacher/grades', element: <TeacherGradesPage /> },
  { path: '/teacher/homework', element: <TeacherHomeworkPage /> },
  { path: '/teacher/schedule', element: <TeacherSchedulePage /> },
  { path: '/teacher/communication', element: <TeacherCommunicationPage /> },

  // Catch‑all route
  { path: '*', element: <NotFoundPage /> },
];

export default routes;
